import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Table, Pagination, Form, Row, Col } from 'react-bootstrap';

function PurchaseRequestsTable({ requests }) {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortField, setSortField] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');

    // support both shapes: { requests: [...] } and direct array
    const rows = useMemo(() => {
        return Array.isArray(requests) ? requests : (requests && Array.isArray(requests.requests) ? requests.requests : []);
    }, [requests]);

    // Sorting logic
    const sortedRows = useMemo(() => {
        if (!rows || rows.length === 0) return [];
        
        return [...rows].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortField) {
                case 'required_by_date':
                    aValue = new Date(a.required_by_date);
                    bValue = new Date(b.required_by_date);
                    break;
                case 'priority':
                    const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
                    aValue = priorityOrder[a.priority] || 0;
                    bValue = priorityOrder[b.priority] || 0;
                    break;
                case 'requested_by':
                    aValue = (a.requested_by?.name || '').toLowerCase();
                    bValue = (b.requested_by?.name || '').toLowerCase();
                    break;
                case 'created_at':
                default:
                    aValue = new Date(a.created_at);
                    bValue = new Date(b.created_at);
                    break;
            }
            
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [rows, sortField, sortDirection]);

    // Pagination logic
    const totalPages = Math.ceil(sortedRows.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedRows = sortedRows.slice(startIndex, startIndex + itemsPerPage);

    // Handle sorting
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setCurrentPage(1); // Reset to first page when sorting
    };

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Generate pagination items
    const getPaginationItems = () => {
        const items = [];
        const maxVisiblePages = 5;
        
        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                items.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) items.push(i);
                items.push('...');
                items.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                items.push(1);
                items.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) items.push(i);
            } else {
                items.push(1);
                items.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) items.push(i);
                items.push('...');
                items.push(totalPages);
            }
        }
        return items;
    };

    if (!rows || rows.length === 0) {
        return <p>No purchase request found.</p>;
    }

    return (
        <div>
            {/* Table controls */}
            <Row className="mb-3 align-items-center">
                <Col md={6}>
                    <div className="d-flex align-items-center">
                        <span className="me-2">Show</span>
                        <Form.Select 
                            size="sm" 
                            value={itemsPerPage} 
                            onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            style={{ width: 'auto' }}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </Form.Select>
                        <span className="ms-2">entries</span>
                    </div>
                </Col>
                <Col md={6} className="text-end">
                    <small className="text-muted">
                        Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, sortedRows.length)} of {sortedRows.length} entries
                    </small>
                </Col>
            </Row>

            {/* Responsive table */}
            <div className="table-responsive">
                <Table bordered hover size="sm" className="mb-0">
                    <thead className="table-dark">
                        <tr>
                            <th style={{ width: '60px' }}>SN</th>
                            <th style={{ width: '120px' }}>Request No. </th>
                            <th 
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSort('required_by_date')}
                            >
                                Required Date
                                {sortField === 'required_by_date' && (
                                    <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                )}
                            </th>
                            <th 
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSort('priority')}
                            >
                                Priority
                                {sortField === 'priority' && (
                                    <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                )}
                            </th>
                            <th 
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSort('requested_by')}
                            >
                                Requested By
                                {sortField === 'requested_by' && (
                                    <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                )}
                            </th>
                            <th>Request Items</th>
                            <th 
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSort('created_at')}
                            >
                                Created At
                                {sortField === 'created_at' && (
                                    <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                )}
                            </th>
                            <th className="text-end" style={{ width: '100px' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedRows.map((r, i) => (
                            <tr key={r._id}>
                                <td>{startIndex + i + 1}</td>
                                <td>
                                    {r.request_no}
                                    <div>
                                        <span className={`badge badge-phoenix fs-11 badge-phoenix-${r.status == 'rfq_generated' ? 'success' : 'primary'}`}>{r.status == 'rfq_generated' ? 'RFQ Generated' : r.status}</span>
                                    </div>
                                    </td>
                                <td>
                                    {new Date(r.required_by_date).toLocaleDateString('en-IN', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                    })}
                                </td>
                                <td>
                                    <span className={`badge text-bg-${getStatusColor(r.priority)}`}>
                                        {r.priority}
                                    </span>
                                </td>
                                <td>{r.requested_by?.name || '-'}</td>
                                <td>
                                    {Array.isArray(r.items) && r.items.length > 0 ? (
                                        <div className="d-flex flex-column gap-1">
                                            {r.items.slice(0, 2).map((item, index) => (
                                                <div key={index} className="small">
                                                    <strong>{item.product?.productName || 'Unknown Product'}</strong>
                                                    <span className="text-muted ms-2">
                                                        Qty: {item.approx_quantity} {item.product?.unitId?.unitCode || ''}
                                                    </span>
                                                </div>
                                            ))}
                                            {r.items.length > 2 && (
                                                <div className="small text-muted">
                                                    +{r.items.length - 2} more items
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-muted">No items</span>
                                    )}
                                </td>
                                <td>
                                    <span className="d-none d-md-inline">
                                        {new Date(r.created_at).toLocaleDateString('en-IN', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: true
                                        })}
                                    </span>
                                    <span className="d-md-none">
                                        {new Date(r.created_at).toLocaleDateString('en-IN', {
                                            day: '2-digit',
                                            month: 'short'
                                        })}
                                    </span>
                                </td>
                                <td className="text-end">
                                    <Link className="btn btn-sm btn-info" to={`/purchase_requests/${r._id}`}>
                                        View
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                    <Pagination size="sm">
                        <Pagination.Prev 
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                        />
                        
                        {getPaginationItems().map((item, index) => (
                            item === '...' ? (
                                <Pagination.Ellipsis key={`ellipsis-${index}`} />
                            ) : (
                                <Pagination.Item
                                    key={item}
                                    active={currentPage === item}
                                    onClick={() => handlePageChange(item)}
                                >
                                    {item}
                                </Pagination.Item>
                            )
                        ))}
                        
                        <Pagination.Next 
                            disabled={currentPage === totalPages}
                            onClick={() => handlePageChange(currentPage + 1)}
                        />
                    </Pagination>
                </div>
            )}
        </div>
    );
}

// Optional: Color-coded status badges
function getStatusColor(status) {
    switch (status) {
        case 'high':
            return 'danger';
        case 'medium':
            return 'warning';
        case 'low':
            return 'secondary';
        default:
            return 'light';
    }
}

export default PurchaseRequestsTable;
